import { EventEmitter, Injectable } from '@angular/core';
import { BaseHttpService } from '@shared/services/base-http.service';
import { Observable } from 'rxjs';
import { Response } from '@shared/models/response.model';
import { map } from 'rxjs/operators';
import { CartListData } from '@shared/interfaces/cart/cart-list-data.interface';
import { PayReq } from '@shared/interfaces/cart/pay-req.interface';
import { AvalibleMethods } from '@shared/interfaces/cart/avalible-methods.interface';
import { PaymentMethodTPay } from '@shared/interfaces/cart/paymentMethodTPay.interface';
import { CurrencyRate } from '@shared/interfaces/cart/currencyRate.interface';
import { Transaction } from '@shared/interfaces/cart/transaction.interface';
import { RoutesPath } from '@shared/routes-path';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  callUpdateData: EventEmitter<CartListData> = new EventEmitter();

  constructor(public baseHttp: BaseHttpService) {}

  getCartList(): Observable<Response<CartListData>> {
    return this.baseHttp.get('/cart').pipe(map((resp) => resp as Response<CartListData>));
  }

  addItem(pack): Observable<Response<CartListData>> {
    return this.baseHttp
      .put('/cart/add-item', pack)
      .pipe(map((resp) => resp as Response<CartListData>));
  }

  removeItem(id): Observable<Response<CartListData>> {
    return this.baseHttp
      .delete('/cart/remove-item/' + id)
      .pipe(map((resp) => resp as Response<CartListData>));
  }

  clearCart(): Observable<Response<any[]>> {
    return this.baseHttp.delete('/cart/clear-cart').pipe(map((resp) => resp as Response<any[]>));
  }

  allAnonymous(pack): Observable<Response<CartListData>> {
    return this.baseHttp
      .post('/cart/proceed', { is_anonymous: pack })
      .pipe(map((resp) => resp as Response<CartListData>));
  }

  changeAmount(pack): Observable<Response<CartListData>> {
    return this.baseHttp
      .post('/cart/change-amount/' + pack.project_id, { amount: pack.amount })
      .pipe(map((resp) => resp as Response<CartListData>));
  }

  changeAmountAll(pack): Observable<Response<CartListData>> {
    return this.baseHttp
      .post('/cart/change-amount-all', { amount: pack.amount })
      .pipe(map((resp) => resp as Response<CartListData>));
  }

  paymentMethods(): Observable<Response<AvalibleMethods>> {
    return this.baseHttp
      .get('/payments/available-methods')
      .pipe(map((resp) => resp as Response<AvalibleMethods>));
  }

  paymentMethodsPayPal(): Observable<Response<AvalibleMethods>> {
    return this.baseHttp
        .get('/payments/paypal/channels')
        .pipe(map((resp) => resp as Response<AvalibleMethods>));
  }

  paymentMethodsTpay(): Observable<Response<PaymentMethodTPay[]>> {
    return this.baseHttp
      .get('/payments/tpay/channels')
      .pipe(map((resp) => resp as Response<PaymentMethodTPay[]>));
  }

  paymentCurrenciesRates(): Observable<Response<CurrencyRate[]>> {
    return this.baseHttp
      .get('/currencies/rates')
      .pipe(map((resp) => resp as Response<CurrencyRate[]>));
  }

  pay(pack): Observable<Response<PayReq>> {
    return this.baseHttp
      .post('/payments/project/cart', pack)
      .pipe(map((resp) => resp as Response<PayReq>));
  }

  payOne(pack): Observable<Response<PayReq>> {
    return this.baseHttp
      .post('/payments/project', pack)
      .pipe(map((resp) => resp as Response<PayReq>));
  }

  payTpay(pack, id): Observable<Response<PayReq>> {
    const URL = `${window.location.origin}/cart/${RoutesPath.default.transactionStatus}`;
    pack.success_url = URL;
    pack.error_url = URL;
    return this.baseHttp
      .post(`/payments/tpay/projects/${id}/donate`, pack)
      .pipe(map((resp) => resp as Response<PayReq>));
  }

  payPayPal(pack, id): Observable<Response<PayReq>> {
    return this.baseHttp
        .post(`/payments/paypal/projects/${id}/donate`, pack)
        .pipe(map((resp) => resp as Response<PayReq>));
  }

  payOneAndCreate(pack): Observable<Response<PayReq>> {
    return this.baseHttp
      .post('/payments/project/pay-and-create', pack, { headers: 'Access-Control-Allow-Origin' })
      .pipe(map((resp) => resp as Response<PayReq>));
  }

  transaction(pack): Observable<Response<Transaction>> {
    return this.baseHttp
      .post('/payments/check-transaction', pack)
      .pipe(map((resp) => resp as Response<Transaction>));
  }

  transactionOrder(uuid): Observable<Response<Transaction>> {
    return this.baseHttp
      .get(`/orders/${uuid}/status`)
      .pipe(map((resp) => resp as Response<Transaction>));
  }

  transactionWithoutToken(pack): Observable<Response<Transaction>> {
    return this.baseHttp
      .post('/payments/check-transaction/without-token', pack)
      .pipe(map((resp) => resp as Response<Transaction>));
  }

  transactionDetails(pack): Observable<Response<Transaction>> {
    return this.baseHttp
      .post('/payments/transaction-details', pack)
      .pipe(map((resp) => resp as Response<Transaction>));
  }

  payPalTransactionDetails(pack): Observable<Response<Transaction>> {
    return this.baseHttp
        .post('/payments/paypal/transaction-details', pack)
        .pipe(map((resp) => resp as Response<Transaction>));
  }
}
